'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { SearchDropdown } from './SearchDropdown';
import type { AutocompleteResult } from '@/validators/search.validator';
const DEBOUNCE_MS = 200;
function SearchIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
interface SearchBarProps {
  /**
   * 'header' is the compact bar in the desktop header; 'page' is the
   * full-width bar on /search — the only search input phones ever see,
   * since the header bar is hidden below the md breakpoint.
   */
  variant?: 'header' | 'page';
  /** Focus the input on mount (used when the mobile header panel opens). */
  autoFocus?: boolean;
  /** Called after any navigation the bar triggers (submit or result click). */
  onNavigate?: () => void;
}
export function SearchBar({
  variant = 'header',
  autoFocus = false,
  onNavigate
}: SearchBarProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AutocompleteResult | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Prefill the input from ?q= after mount (relevant on /search results).
  // Deliberately reads window.location instead of useSearchParams(): the
  // header renders on every page, and useSearchParams() would force a
  // Suspense boundary around it, breaking static prerendering site-wide.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) setQuery(q);
  }, []);
  const totalItems = results ? results.pages.length + results.categories.length + results.products.length : 0;
  const fetchAutocomplete = useCallback(async (q_0: string) => {
    if (q_0.length < 1) {
      setResults(null);
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`/api/search/autocomplete?q=${encodeURIComponent(q_0)}`);
      if (res.ok) {
        const data: AutocompleteResult = await res.json();
        setResults(data);
        setIsOpen(true);
        setActiveIndex(-1);
      }
    } catch {
      // Silently fail — search is not critical
    } finally {
      setIsLoading(false);
    }
  }, []);
  const handleInputChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchAutocomplete(value), DEBOUNCE_MS);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setIsOpen(false);
      router.push(`/search?q=${encodeURIComponent(query.trim())}`);
      onNavigate?.();
    }
  };
  const handleKeyDown = (e_0: React.KeyboardEvent) => {
    if (!isOpen || !results) return;
    if (e_0.key === 'ArrowDown') {
      e_0.preventDefault();
      setActiveIndex(prev => prev < totalItems - 1 ? prev + 1 : 0);
    } else if (e_0.key === 'ArrowUp') {
      e_0.preventDefault();
      setActiveIndex(prev_0 => prev_0 > 0 ? prev_0 - 1 : totalItems - 1);
    } else if (e_0.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    } else if (e_0.key === 'Enter' && activeIndex >= 0) {
      e_0.preventDefault();
      const allItems = [...results.pages.map(p => p.url), ...results.categories.map(c => c.url), ...results.products.map(p_0 => p_0.url)];
      if (allItems[activeIndex]) {
        setIsOpen(false);
        router.push(allItems[activeIndex]);
        onNavigate?.();
      }
    }
  };

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e_1: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e_1.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  return <div ref={containerRef} className="relative">
      <form onSubmit={handleSubmit} className="relative">
        <input ref={inputRef} type="text" value={query} onChange={e_2 => handleInputChange(e_2.target.value)} onKeyDown={handleKeyDown} onFocus={() => {
        if (results && query.length >= 1) setIsOpen(true);
      }} placeholder="Search here..." className={variant === 'page' ? 'w-full h-10 pl-4 pr-10 text-base border border-gray-300 rounded focus:outline-none focus:border-[#ED8326] focus:ring-1 focus:ring-[#ED8326]' : 'w-40 lg:w-48 h-8 pl-3 pr-8 text-sm border border-gray-300 rounded focus:outline-none focus:border-[#ED8326] focus:ring-1 focus:ring-[#ED8326]'} aria-label="Search products" autoComplete="off"
      // eslint-disable-next-line jsx-a11y/no-autofocus -- only set when the user just tapped the search toggle
      autoFocus={autoFocus} />
        <button type="submit" className={`absolute right-0 top-0 flex items-center justify-center text-gray-500 hover:text-[#ED8326] ${variant === 'page' ? 'h-10 w-10' : 'h-8 w-8'}`} aria-label="Search">
          {isLoading ? <span className="h-4 w-4 border-2 border-gray-300 border-t-[#ED8326] rounded-full animate-spin" /> : <SearchIcon className="h-4 w-4" />}
        </button>
      </form>

      {isOpen && results && <SearchDropdown results={results} query={query} onClose={() => {
      setIsOpen(false);
      onNavigate?.();
    }} activeIndex={activeIndex} />}
    </div>;
}